import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

// The Break Room's "DO NOT PRESS" button. Purely cosmetic: it shakes and blows up
// #root with CSS, then covers the screen with a "you broke it" page. Nothing is
// saved or sent anywhere — a refresh brings the site back exactly as it was.

const COUNT_FROM = 5;
const BOOM_MS = 1800;
const DEBRIS = ['💥', '🔥', '🧱', '🔩', '⚙️', '💨', '📎', '☕', '📌', '🪵'];

const CSS = `
  @keyframes sd-shake {
    0%,100% { transform: translate(0,0) rotate(0); }
    20% { transform: translate(calc(var(--sd-amp) * -1px), calc(var(--sd-amp) * 0.6px)) rotate(-0.4deg); }
    40% { transform: translate(calc(var(--sd-amp) * 1px), calc(var(--sd-amp) * -0.8px)) rotate(0.5deg); }
    60% { transform: translate(calc(var(--sd-amp) * -0.7px), calc(var(--sd-amp) * -0.5px)) rotate(-0.3deg); }
    80% { transform: translate(calc(var(--sd-amp) * 0.9px), calc(var(--sd-amp) * 0.7px)) rotate(0.4deg); }
  }
  @keyframes sd-blowup {
    0%   { transform: scale(1) rotate(0); filter: none; opacity: 1; }
    15%  { transform: scale(1.06) rotate(-1deg); filter: brightness(2.5) saturate(0); }
    100% { transform: scale(2.6) rotate(14deg) translateY(30%); filter: blur(14px) brightness(3); opacity: 0; }
  }
  @keyframes sd-pulse { 0%,100% { opacity: 0.25; } 50% { opacity: 0.6; } }
  @keyframes sd-tick { 0% { transform: scale(2.2); opacity: 0; } 25% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.85); opacity: 0.9; } }
  @keyframes sd-flash { 0% { opacity: 0; } 8% { opacity: 1; } 100% { opacity: 0; } }
  @keyframes sd-fireball {
    0% { transform: translate(-50%,-50%) scale(0); opacity: 1; }
    60% { opacity: 1; }
    100% { transform: translate(-50%,-50%) scale(9); opacity: 0; }
  }
  @keyframes sd-debris {
    0% { transform: translate(-50%,-50%) rotate(0) scale(0.6); opacity: 1; }
    100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(1.4); opacity: 0; }
  }
  @keyframes sd-fadein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  @keyframes sd-smoke { 0% { transform: translateY(0) scale(1); opacity: 0.35; } 100% { transform: translateY(-120px) scale(2.2); opacity: 0; } }
  @keyframes sd-flicker { 0%,19%,21%,60%,62%,100% { opacity: 1; } 20%,61% { opacity: 0.35; } }

  #root.sd-shaking { animation: sd-shake 0.18s linear infinite; }
  #root.sd-boom { animation: sd-blowup ${BOOM_MS * 0.7}ms cubic-bezier(.3,.0,.7,1) forwards; pointer-events: none; }
  #root.sd-gone { visibility: hidden; }
`;

export function DoNotPressButton({ onPress }) {
  return (
    <button
      onClick={onPress}
      className="shrink-0 flex flex-col items-center group"
      title="Seriously. Don't."
    >
      <span
        className="block rounded-full transition-transform group-hover:scale-105 group-active:scale-95"
        style={{
          width: 74, height: 74,
          background: 'radial-gradient(circle at 35% 30%, #ff8a80, #e53935 45%, #8e0000 100%)',
          border: '5px solid #3a3a3a',
          boxShadow: '0 6px 0 #1f1f1f, 0 10px 18px rgba(0,0,0,0.45), inset 0 -6px 10px rgba(0,0,0,0.35)',
        }}
      />
      <span
        className="mt-2 px-2 py-0.5 text-[11px] font-black uppercase tracking-widest"
        style={{ background: '#ffd600', color: '#1a1a1a', border: '2px solid #1a1a1a', backgroundImage: 'repeating-linear-gradient(45deg, #ffd600 0 8px, #ffeb3b 8px 16px)' }}
      >
        Do Not Press
      </span>
    </button>
  );
}

export default function SelfDestruct() {
  const [phase, setPhase] = useState('countdown'); // countdown → boom → broken
  const [n, setN] = useState(COUNT_FROM);

  const debris = useMemo(() => Array.from({ length: 46 }, (_, i) => {
    const angle = Math.random() * Math.PI * 2;
    const dist = 300 + Math.random() * 700;
    return {
      e: DEBRIS[i % DEBRIS.length],
      dx: `${Math.cos(angle) * dist}px`,
      dy: `${Math.sin(angle) * dist}px`,
      rot: `${(Math.random() - 0.5) * 1440}deg`,
      size: 22 + Math.random() * 40,
      delay: Math.random() * 120,
      dur: 900 + Math.random() * 700,
    };
  }), []);

  // Countdown, one tick a second
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (n === 0) { setPhase('boom'); return; }
    const t = setTimeout(() => setN((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, n]);

  useEffect(() => {
    if (phase !== 'boom') return;
    const t = setTimeout(() => setPhase('broken'), BOOM_MS);
    return () => clearTimeout(t);
  }, [phase]);

  // Shake / blow up / hide the real page. Classes are removed on unmount so nothing lingers.
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    root.classList.remove('sd-shaking', 'sd-boom', 'sd-gone');
    if (phase === 'countdown') {
      root.style.setProperty('--sd-amp', String((COUNT_FROM - n + 1) * 1.6));
      root.classList.add('sd-shaking');
    } else if (phase === 'boom') {
      root.classList.add('sd-boom');
    } else {
      root.classList.add('sd-gone');
    }
    document.body.style.overflow = 'hidden';
  }, [phase, n]);

  useEffect(() => () => {
    const root = document.getElementById('root');
    root?.classList.remove('sd-shaking', 'sd-boom', 'sd-gone');
    root?.style.removeProperty('--sd-amp');
    document.body.style.overflow = '';
  }, []);

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 2147483000 }}>
      <style>{CSS}</style>

      {phase === 'countdown' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center select-none" style={{ cursor: 'not-allowed' }}>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(255,0,0,0.15), rgba(160,0,0,0.85))', animation: 'sd-pulse 0.5s ease-in-out infinite' }} />
          <p className="relative text-white font-black uppercase tracking-[0.3em] text-lg sm:text-2xl text-center px-4" style={{ textShadow: '0 0 12px #ff1744', animation: 'sd-flicker 1.3s linear infinite' }}>
            ⚠️ Self-destruct sequence initiated ⚠️
          </p>
          <div key={n} className="relative font-black text-white tabular-nums leading-none mt-4" style={{ fontSize: 'min(42vw, 260px)', textShadow: '0 0 30px #ff1744, 0 0 80px #ff1744', animation: 'sd-tick 1s ease-out forwards' }}>
            {n}
          </div>
          <p className="relative text-red-100 font-bold mt-4 text-sm sm:text-base">Please do not panic. (Panic.)</p>
        </div>
      )}

      {phase === 'boom' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute rounded-full" style={{ left: '50%', top: '50%', width: 200, height: 200, background: 'radial-gradient(circle, #fff 0%, #ffeb3b 25%, #ff9800 50%, #e53935 70%, transparent 72%)', animation: `sd-fireball ${BOOM_MS * 0.8}ms ease-out forwards` }} />
          {debris.map((d, i) => (
            <span key={i} className="absolute" style={{ left: '50%', top: '50%', fontSize: d.size, '--dx': d.dx, '--dy': d.dy, '--rot': d.rot, animation: `sd-debris ${d.dur}ms cubic-bezier(.1,.6,.3,1) ${d.delay}ms forwards` }}>
              {d.e}
            </span>
          ))}
          <div className="absolute inset-0 bg-white" style={{ animation: `sd-flash ${BOOM_MS * 0.6}ms ease-out forwards` }} />
        </div>
      )}

      {phase === 'broken' && (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden px-4" style={{ background: 'radial-gradient(ellipse at center, #2a2a2a, #0b0b0b)' }}>
          {/* cracks */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ opacity: 0.35 }}>
            <g stroke="#ddd" strokeWidth="0.15" fill="none">
              <path d="M50 48 L30 30 L22 12 L18 0" /><path d="M50 48 L70 25 L78 8 L90 0" />
              <path d="M50 48 L85 45 L100 40" /><path d="M50 48 L72 70 L80 88 L84 100" />
              <path d="M50 48 L45 75 L38 100" /><path d="M50 48 L20 62 L0 70" />
              <path d="M50 48 L12 40 L0 38" /><path d="M30 30 L12 40" /><path d="M70 25 L85 45" />
              <path d="M72 70 L45 75" /><path d="M20 62 L45 75" />
            </g>
          </svg>
          {/* smoke */}
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="absolute rounded-full" style={{ left: `${10 + i * 11}%`, bottom: -40, width: 120, height: 120, background: 'radial-gradient(circle, rgba(140,140,140,0.5), transparent 70%)', animation: `sd-smoke ${4 + (i % 3)}s ease-out ${i * 0.6}s infinite` }} />
          ))}
          <div className="relative text-center max-w-xl" style={{ animation: 'sd-fadein 0.8s ease-out forwards' }}>
            <div className="text-7xl mb-4">💥🫠</div>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-4">Why did you press the button?</h1>
            <p className="text-xl sm:text-2xl font-bold text-gray-300 mb-2">See what you did?</p>
            <p className="text-2xl sm:text-4xl font-black" style={{ color: '#ff5252', animation: 'sd-flicker 2.5s linear infinite' }}>You broke it.</p>
            <p className="text-xs text-gray-600 mt-10 italic">It said “Do Not Press.” It was very clear about that.</p>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
